import { BACKEND_URL } from "../../views/common/utils/constants";
import {
  IAPIResponse,
  IBankAccount,
  IBvnData,
  INinData,
  IPaymentInvoiceData,
  IPurchaseRequest,
} from "../../views/common/utils/interfaces";
import {
  errorToastify,
  successToastify,
} from "../../views/common/utils/Toasty";
import { extractErrorMsg } from "../../views/common/utils/validation";
import { CustomAxios } from "../customAxios";

export const getPaymentAmount = async () => {
  try {
    const { data: result }: IAPIResponse = await CustomAxios().get(
      `${BACKEND_URL}/payments/types`
    );
    if (!result.status) {
      throw new Error(result.message);
    }
    return result;
  } catch (error) {
    if (typeof error === "string") {
      errorToastify(error);
    }
  }
};

export const getBankAccount = async () => {
  try {
    const { data: result }: IAPIResponse = await CustomAxios().get(
      `${BACKEND_URL}/payments/bank-account`
    );
    if (!result.status) {
      throw new Error(result.message);
    }
    return result?.data as IBankAccount;
  } catch (error: any) {
    if (error.response?.data?.message) {
      return error.response.data as IBankAccount;
    } else if (typeof error === "string") {
      errorToastify(error);
    }

    return null;
  }
};

export const getPaymentInvoiceData = async ({
  queryKey,
}: {
  queryKey: any[];
}) => {
  const referenceId = queryKey[1]?.referenceId;

  if (!referenceId) return;

  try {
    const { data: result }: IAPIResponse = await CustomAxios().get(
      `${BACKEND_URL}/payments/purchase-requests/${referenceId}/get-invoice-data`
    );
    if (!result.status) {
      throw new Error(result.message);
    }
    return result?.data as IPaymentInvoiceData;
  } catch (error) {
    if (typeof error === "string") {
      errorToastify(error);
    }

    return null;
  }
};

export const initiatePayment = async (id: string, body: Record<string, any>) => {
  try {
    const { data: result }: IAPIResponse = await CustomAxios().post(
      `${BACKEND_URL}/payments/${id}/initiate-payment`,
      body
    );
    if (!result.status) {
      throw new Error(result.message);
    }
    return result;
  } catch (error: any) {
    if (typeof error === "string") {
      errorToastify(error);
    } else {
      errorToastify(error.response.message || error.response.data.message);
    }
  }
};

export const completePayment = async (status: boolean, reference: string) => {
  try {
    const { data: result }: IAPIResponse = await CustomAxios().post(
      `${BACKEND_URL}/payments/t/${reference}/complete-payment`,
      {status}
    );
    if (!result.status) {
      throw new Error(result.message);
    }
    successToastify(result.message)
    return result;
  } catch (error: any) {
    if (typeof error === "string") {
      errorToastify(error);
    } else {
      errorToastify(error.response.message || error.response.data.message);
    }
  }
};

export const validateBvn = async (body: { bvn: string }) => {
  try {
    const { data: result }: IAPIResponse = await CustomAxios().post(
      `${BACKEND_URL}/payments/validate-bvn`,
      body
    );
    if (!result.status) {
      throw new Error(result.message);
    }

    successToastify(result.message);
    return result?.data as IBvnData;
  } catch (error: any) {
    if (typeof error === "string") {
      errorToastify(error);
    } else {
      errorToastify(error.response.message || error.response.data.message);
    }
    return "error";
  }
};
export const validateNin = async (body: { nin: string }) => {
  try {
    const { data: result }: IAPIResponse = await CustomAxios().post(
      `${BACKEND_URL}/payments/validate-nin`,
      body
    );
    if (!result.status) {
      throw new Error(result.message);
    }

    successToastify(result.message);
    return result?.data as INinData;
  } catch (error: any) {
    if (typeof error === "string") {
      errorToastify(error);
    } else {
      errorToastify(error.response.message || error.response.data.message);
    }
    return "error";
  }
};

export const postPurchaseRequest = async (body: FormData) => {
  const listingId = body.get("listingId");

  try {
    const { data: result }: IAPIResponse = await CustomAxios().post(
      `${BACKEND_URL}/listings/${listingId}/submit-purchase-request`,
      body,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    if (!result.status) {
      throw new Error(result.message);
    }
    return result?.data as IPurchaseRequest;
  } catch (error: any) {
    if (typeof error === "string") {
      errorToastify(error);
    } else {
      errorToastify(error.response.message || error.response.data.message);
    }
    return "error";
  }
};

export const postNewBankAccount = async (body: { bvn: string }) => {
  try {
    const { data: result }: IAPIResponse = await CustomAxios().post(
      `${BACKEND_URL}/payments/accounts/open`,
      body
    );
    if (!result.status) {
      throw new Error(result.message);
    }
    return result?.data as any;
  } catch (error: any) {
    if (typeof error === "string") {
      errorToastify(error);
    } else {
      errorToastify(error.response.message || error.response.data.message);
      if (error.response.data.errors) {
        return error.response.data;
      }
    }
    return "error";
  }
};

export const updateNewBankAccount = async (body: FormData) => {
  const referenceId = body.get("reference_id");

  try {
    const { data: result }: IAPIResponse = await CustomAxios().put(
      `${BACKEND_URL}/payments/accounts/${referenceId}/finish`,
      body,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    if (!result.status) {
      throw new Error(result.message);
    }
    return result?.data as any;
  } catch (error: any) {
    if (typeof error === "string") {
      errorToastify(error);
    } else {
      errorToastify(error.response.message || error.response.data.message);
      if (error.response.data.data) {
        return { errors: error.response.data.data };
      }
    }
    return "error";
  }
};
export const confirmPayment = async (body: { referenceId: string }) => {
  const { referenceId } = body;

  try {
    const { data: result }: IAPIResponse = await CustomAxios().post(
      `${BACKEND_URL}/payments/${referenceId}/confirm-payment`,
      body
    );
    if (!result.status) {
      throw new Error(result.message);
    }

    successToastify(result.message);
    return result?.data as INinData;
  } catch (error: any) {
    if (typeof error === "string") {
      errorToastify(error);
    } else {
      errorToastify(error.response.message || error.response.data.message);
    }
    return "error";
  }
};
